/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-extraneous-class */

import RandomDataGenerator from '../libs/RandomDataGenerator';

export interface XY {
  x: number;
  y: number;
}

export interface RectangleData {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * A collection of math utility functions.
 */
export default class MathUtils {
  public static rnd = new RandomDataGenerator([Date.now()]);

  /**
   * Converts radians to degrees.
   */
  public static radsToDegrees(rads: number): number {
    return (180 * rads) / Math.PI;
  }

  /**
   * Converts degrees to radians.
   */
  public static degreesToRads(degrees: number): number {
    return (degrees * Math.PI) / 180;
  }

  /**
   * Get the distance between two points.
   */
  public static getDistance(x1: number, y1: number, x2: number, y2: number): number {
    return Math.sqrt((x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2));
  }

  /**
   * Get the distance between two points.
   */
  public static getDistanceBetween(pointA: { x: number; y: number }, pointB: { x: number; y: number }): number {
    return this.getDistance(pointA.x, pointA.y, pointB.x, pointB.y);
  }

  /**
   * Gets the point on the circles circumference at the given angle.
   */
  public static getPointOnCircle(
    centerX: number,
    centerY: number,
    radius: number,
    angle: number,
    out: XY = { x: 0, y: 0 },
  ): XY {
    out.x = centerX + radius * Math.cos(angle);
    out.y = centerY + radius * Math.sin(angle);
    return out;
  }

  /**
   * Get unit vector from vector x,y values.
   */
  public static getUnitVector(x: number, y: number, out: XY = { x: 0, y: 0 }): XY {
    const length = this.getVectorLength(x, y);
    out.x = x / length;
    out.y = y / length;
    return out;
  }

  /**
   * Get the inverse of a vector.
   */
  public static getInverseVector(x: number, y: number, out: XY = { x: 0, y: 0 }): XY {
    out.x = -x;
    out.y = -y;
    return out;
  }

  /**
   * Get length of a vector.
   */
  public static getVectorLength(x: number, y: number): number {
    return Math.sqrt(x * x + y * y);
  }

  /**
   * Get the unit vector of a angle with the specified length.
   */
  public static getVectorFromAngle(angle: number, out: XY = { x: 0, y: 0 }, vectorLength = 1): XY {
    out.x = Math.cos(angle) * vectorLength;
    out.y = Math.sin(angle) * vectorLength;
    return out;
  }

  /**
   * Get angle from a vectors rise / run.
   */
  public static getVectorAngle(x: number, y: number): number {
    const angle = Math.atan2(y, x);
    return angle < 0 ? angle + Math.PI * 2 : angle;
  }

  /**
   * Get angle between from one vector to another.
   */
  public static getAngleToVector(v1x: number, v1y: number, v2x: number, v2y: number): number {
    return Math.atan2(v2y - v1y, v2x - v1x);
  }

  public static isStandardBasisVector(x: number, y: number): boolean {
    return (x === 1 && y === 0) || (x === 0 && y === 1);
  }

  /**
   * Find intersection of 2 line segments.
   */
  public static checkLineIntersection(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    x3: number,
    y3: number,
    x4: number,
    y4: number,
    out?: XY,
  ): boolean {
    const d = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    // If denominator is zero, there is no intersection
    if (d === 0) return false;

    // Get the x and y of the intersection
    const pre = x1 * y2 - y1 * x2;
    const post = x3 * y4 - y3 * x4;
    const x = (pre * (x3 - x4) - (x1 - x2) * post) / d;
    const y = (pre * (y3 - y4) - (y1 - y2) * post) / d;

    // Check if the x and y coordinates are within both lines
    if (x < Math.min(x1, x2) || x > Math.max(x1, x2) || x < Math.min(x3, x4) || x > Math.max(x3, x4)) {
      return false;
    }
    if (y < Math.min(y1, y2) || y > Math.max(y1, y2) || y < Math.min(y3, y4) || y > Math.max(y3, y4)) {
      return false;
    }

    // update output point if set
    if (out !== undefined) {
      out.x = x;
      out.y = y;
    }
    return true;
  }

  /**
   * Checks if a point is contained within a triangle.
   * @param p XY to check
   * @param a triangle XY 1
   * @param b triangle XY 2
   * @param c triangle XY 3
   */
  public static isPointInTriangle(p: XY, a: XY, b: XY, c: XY): boolean {
    const denominator = (b.y - c.y) * (a.x - c.x) + (c.x - b.x) * (a.y - c.y);
    const alpha = ((b.y - c.y) * (p.x - c.x) + (c.x - b.x) * (p.y - c.y)) / denominator;
    const beta = ((c.y - a.y) * (p.x - c.x) + (a.x - c.x) * (p.y - c.y)) / denominator;
    const gamma = 1 - alpha - beta;
    return alpha >= 0 && beta >= 0 && gamma >= 0;
  }

  /**
   * Check if point p is left of line a->b.
   */
  public static isPointLeftOfLine(px: number, py: number, ax: number, ay: number, bx: number, by: number): boolean {
    const crossProduct = (bx - ax) * (py - ay) - (by - ay) * (px - ax);
    return crossProduct > 0;
  }

  /**
   * Limits the angle to be between 0 and 2*PI (360 degrees) radians keeping the direction.
   */
  public static limitAngleRad(angle: number): number {
    const revolutions = Math.floor(angle / (Math.PI * 2));
    return angle - revolutions * (Math.PI * 2);
  }

  /**
   * Limits the angle to be between 0 and 360 degrees keeping the direction.
   */
  public static limitAngleDeg(angle: number): number {
    const revolutions = Math.floor(angle / 360);
    return angle - revolutions * 360;
  }

  /**
   * Finds the shortest rotation from fromAngle -> toAngle.
   */
  public static findShortestRotationRads(fromAngle: number, toAngle: number): number {
    let toRotate = this.limitAngleRad(toAngle) - this.limitAngleRad(fromAngle);
    // invert the rotation if toRotate is > then PI in either direction
    if (toRotate > Math.PI) toRotate = -(Math.PI - toRotate);
    else if (toRotate < -Math.PI) toRotate = Math.PI + toRotate;
    return toRotate;
  }

  /**
   * Calculates the position along a bézier curve.
   * @param p0 start point
   * @param p1 control point
   * @param p2 to point
   * @param t time 0-1
   * @private
   */
  public static getBezierCurvePosition(p0: XY, p1: XY, p2: XY, t: number): XY {
    const x = (1 - t) ** 2 * p0.x + 2 * (1 - t) * t * p1.x + t ** 2 * p2.x;
    const y = (1 - t) ** 2 * p0.y + 2 * (1 - t) * t * p1.y + t ** 2 * p2.y;
    return { x, y };
  }

  /**
   * Get random a random value, where the value is >= minValue && <= maxValue.
   * @returns {number}
   */
  public static getRandomValueInRange(
    minValue: number,
    maxValue: number,
    getRandomNumber: () => number = Math.random,
  ): number {
    return minValue + getRandomNumber() * (maxValue - minValue);
  }

  /**
   * Clamps the value between two given values.
   */
  public static clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  /**
   * Checks to see if a number is between two numbers with a margin.
   */
  public static isBetween(value: number, min: number, max: number, margin = 0): boolean {
    const diff = max - min;
    const calculatedMargin = diff * margin;
    return value >= min - calculatedMargin && value <= max + calculatedMargin;
  }

  public static comparePoints(a: XY, b: XY): boolean {
    return Math.abs(a.x - b.x) < Number.EPSILON && Math.abs(a.y - b.y) < Number.EPSILON;
  }

  public static getAverage(array: number[]): number {
    if (array.length === 0) return 0;
    return array.reduce((a, b) => a + b, 0) / array.length;
  }

  public static getMedian(arr: number[]): number {
    if (!Array.isArray(arr) || arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }
}
